import React from "react";
import styled from "styled-components";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { TAP_TARGET } from "@/shared/theme/breakpoints";
import { pressedDarken } from "@/shared/theme/pressed";

/*
 * The phone chart's period header: previous and next arrows around the period's title. The Weekly
 * view steps through weeks, the Monthly view through months.
 */

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;

const Title = styled.h4`
	margin: 0;
	color: #222222;
	font-size: clamp(13px, 3.8vw, 15px);
	font-weight: 600;
	text-align: center;
	white-space: nowrap;
`;

const Arrow = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${TAP_TARGET}px;
	height: ${TAP_TARGET}px;
	border: none;
	border-radius: 50%;
	background: #f3f5ff;
	color: #303030;
	cursor: pointer;

	&:disabled {
		color: #c7cbe0;
		cursor: default;
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}

	${pressedDarken}
`;

interface PeriodNavProps {
	title: string;
	/** The period's unit, for the arrows' names ("Previous week", "Next month"). */
	unit: "week" | "month";
	onPrevious: () => void;
	onNext: () => void;
	hasPrevious: boolean;
	hasNext: boolean;
}

export const PeriodNav: React.FC<PeriodNavProps> = ({ title, unit, onPrevious, onNext, hasPrevious, hasNext }) => (
	<Header>
		<Arrow type="button" aria-label={`Previous ${unit}`} disabled={!hasPrevious} onClick={onPrevious}>
			<LeftOutlined />
		</Arrow>
		<Title aria-live="polite">{title}</Title>
		<Arrow type="button" aria-label={`Next ${unit}`} disabled={!hasNext} onClick={onNext}>
			<RightOutlined />
		</Arrow>
	</Header>
);
